import { useEffect, useState } from "react";
import { api, EVENT_TYPES, EVENT_TYPE_MAP, EVENT_STATUS, formatApiError } from "@/lib/api";
import DateInput from "@/components/DateInput";
import DateTimeInput from "@/components/DateTimeInput";
import { toast } from "sonner";
import { X, Trash2, Save, Calendar as CalendarIcon, User, Car, Clock, Type, AlignLeft, Tag } from "lucide-react";

/**
 * EventDialog
 * Props:
 *   open: boolean
 *   onClose(refresh?: boolean)
 *   event?: existing event (for edit). When null/undefined => create mode.
 *   defaultStart, defaultEnd: ISO strings used when creating from a slot click
 *   defaultClientId, defaultCarId: pre-fill associations (used from ClientDetail / CarDetail)
 */
export default function EventDialog({ open, onClose, event, defaultStart, defaultEnd, defaultClientId, defaultCarId }) {
  const [form, setForm] = useState(initialForm({}));
  const [clients, setClients] = useState([]);
  const [cars, setCars] = useState([]);
  const [loading, setLoading] = useState(false);

  function initialForm(src) {
    return {
      title: src?.title || "",
      description: src?.description || "",
      event_type: src?.event_type || "custom",
      start_datetime: src?.start_datetime || defaultStart || isoLocalNow(),
      end_datetime: src?.end_datetime || defaultEnd || isoLocalPlus(30),
      all_day: !!src?.all_day,
      client_id: src?.client_id || defaultClientId || "",
      car_id: src?.car_id || defaultCarId || "",
      status: src?.status || "scheduled",
    };
  }

  useEffect(() => {
    if (!open) return;
    setForm(initialForm(event || {}));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, event?.id]);

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const [c, cr] = await Promise.all([api.get("/clients"), api.get("/cars")]);
        setClients(c.data || []);
        setCars(cr.data || []);
      } catch (err) {
        // Lookup data is non-essential for the dialog UX (user can still type freeform)
        // Log for debugging without blocking the user.
        console.error("[EventDialog] failed to load clients/cars:", err);
      }
    })();
  }, [open]);

  if (!open) return null;

  const isEdit = !!event?.id;

  async function save() {
    if (!form.title.trim()) {
      toast.error("Adaugă un titlu pentru eveniment");
      return;
    }
    setLoading(true);
    try {
      const body = {
        title: form.title.trim(),
        description: form.description,
        event_type: form.event_type,
        start_datetime: toUtcIso(form.start_datetime),
        end_datetime: form.all_day ? null : toUtcIso(form.end_datetime),
        all_day: form.all_day,
        client_id: form.client_id || null,
        car_id: form.car_id || null,
        status: form.status,
      };
      if (isEdit) {
        await api.put(`/calendar/events/${event.id}`, body);
        toast.success("Eveniment actualizat");
      } else {
        await api.post(`/calendar/events`, body);
        toast.success("Eveniment creat");
      }
      onClose(true);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }

  async function remove() {
    if (!isEdit) return;
    if (!window.confirm("Ștergi acest eveniment?")) return;
    try {
      await api.delete(`/calendar/events/${event.id}`);
      toast.success("Eveniment șters");
      onClose(true);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  }

  const typeMeta = EVENT_TYPE_MAP[form.event_type] || EVENT_TYPE_MAP.custom;

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center p-0 sm:p-4 overflow-y-auto" onClick={() => onClose(false)} data-testid="event-dialog">
      <div
        className="bg-white w-full sm:max-w-lg rounded-t-2xl sm:rounded-2xl shadow-2xl max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-white z-10 flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <div className="flex items-center gap-2">
            <span className="w-9 h-9 rounded-lg flex items-center justify-center text-lg" style={{ backgroundColor: typeMeta.color + "22", color: typeMeta.color }}>
              {typeMeta.emoji}
            </span>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-400">{isEdit ? "Editare" : "Eveniment nou"}</div>
              <div className="font-display text-lg font-medium leading-tight">{typeMeta.label}</div>
            </div>
          </div>
          <button onClick={() => onClose(false)} className="text-slate-400 hover:text-slate-700 p-1" data-testid="event-dialog-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-5 py-4 space-y-4">
          {/* Title */}
          <div>
            <Label icon={Type}>Titlu</Label>
            <input
              data-testid="event-title-input"
              autoFocus
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="Ex: Test drive BMW X5 cu Ion"
              className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#0047FF]"
            />
          </div>

          {/* Type chips */}
          <div>
            <Label icon={Tag}>Tip activitate</Label>
            <div className="flex flex-wrap gap-2 mt-1">
              {EVENT_TYPES.map((t) => (
                <button
                  key={t.value}
                  type="button"
                  data-testid={`event-type-${t.value}`}
                  onClick={() => setForm({ ...form, event_type: t.value })}
                  className={`inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border transition-all ${
                    form.event_type === t.value
                      ? "border-transparent text-white shadow-sm"
                      : "border-slate-200 hover:border-slate-400 bg-white"
                  }`}
                  style={form.event_type === t.value ? { backgroundColor: t.color } : {}}
                >
                  <span>{t.emoji}</span> {t.label}
                </button>
              ))}
            </div>
          </div>

          {/* Datetime */}
          <div className="grid grid-cols-1 gap-3">
            <div>
              <Label icon={Clock}>Start</Label>
              {form.all_day ? (
                <DateInput
                  testid="event-start-input"
                  value={form.start_datetime.slice(0, 10)}
                  onChange={(v) => setForm((f) => ({ ...f, start_datetime: v, end_datetime: v }))}
                />
              ) : (
                <DateTimeInput
                  testid="event-start-input"
                  value={trimToMinute(form.start_datetime)}
                  onChange={(v) => setForm((f) => ({ ...f, start_datetime: v }))}
                />
              )}
            </div>
            {!form.all_day && (
              <div>
                <Label icon={Clock}>Sfârșit</Label>
                <DateTimeInput
                  testid="event-end-input"
                  value={trimToMinute(form.end_datetime)}
                  onChange={(v) => setForm({ ...form, end_datetime: v })}
                />
              </div>
            )}
          </div>
          <label className="inline-flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
            <input
              data-testid="event-allday"
              type="checkbox"
              checked={form.all_day}
              onChange={(e) => setForm({ ...form, all_day: e.target.checked })}
              className="rounded border-slate-300"
            />
            Toată ziua
          </label>

          {/* Client */}
          <div>
            <Label icon={User}>Client (opțional)</Label>
            <select
              data-testid="event-client-input"
              value={form.client_id}
              onChange={(e) => setForm({ ...form, client_id: e.target.value })}
              className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#0047FF]"
            >
              <option value="">— fără client —</option>
              {clients.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}{c.phone ? ` · ${c.phone}` : ""}
                </option>
              ))}
            </select>
          </div>

          {/* Car */}
          <div>
            <Label icon={Car}>Mașină (opțional)</Label>
            <select
              data-testid="event-car-input"
              value={form.car_id}
              onChange={(e) => setForm({ ...form, car_id: e.target.value })}
              className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#0047FF]"
            >
              <option value="">— fără mașină —</option>
              {cars.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.make} {c.model}{c.year ? ` · ${c.year}` : ""}{c.vin ? ` · VIN ${c.vin.slice(-6)}` : ""}
                </option>
              ))}
            </select>
          </div>

          {/* Description */}
          <div>
            <Label icon={AlignLeft}>Notițe</Label>
            <textarea
              data-testid="event-desc-input"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              rows={3}
              placeholder="Detalii suplimentare..."
              className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#0047FF]"
            />
          </div>

          {/* Status (only for edit) */}
          {isEdit && (
            <div>
              <Label icon={CalendarIcon}>Status</Label>
              <div className="flex gap-2">
                {EVENT_STATUS.map((s) => (
                  <button
                    key={s.value}
                    type="button"
                    data-testid={`event-status-${s.value}`}
                    onClick={() => setForm({ ...form, status: s.value })}
                    className={`flex-1 text-xs px-3 py-2 rounded-lg border transition-all ${
                      form.status === s.value
                        ? s.value === "done"
                          ? "border-emerald-500 bg-emerald-50 text-emerald-700"
                          : s.value === "cancelled"
                          ? "border-rose-500 bg-rose-50 text-rose-700"
                          : "border-[#0047FF] bg-blue-50 text-[#0047FF]"
                        : "border-slate-200 text-slate-600 hover:border-slate-400"
                    }`}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="sticky bottom-0 bg-white border-t border-slate-100 px-5 py-3 flex items-center justify-between">
          <div>
            {isEdit && (
              <button
                data-testid="event-delete-btn"
                onClick={remove}
                className="text-red-600 text-sm inline-flex items-center gap-1.5 hover:underline"
              >
                <Trash2 className="w-4 h-4" /> Șterge
              </button>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => onClose(false)}
              className="px-4 py-2 rounded-lg text-sm border border-slate-200 hover:bg-slate-50"
            >
              Anulează
            </button>
            <button
              data-testid="event-save-btn"
              onClick={save}
              disabled={loading}
              className="bg-[#0047FF] hover:bg-[#003BD3] text-white px-4 py-2 rounded-lg text-sm inline-flex items-center gap-2 disabled:opacity-50"
            >
              <Save className="w-4 h-4" /> {isEdit ? "Salvează" : "Creează"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Label({ icon: Icon, children }) {
  return (
    <label className="text-[10px] uppercase tracking-widest text-slate-400 flex items-center gap-1 mb-1">
      {Icon && <Icon className="w-3 h-3" />}
      {children}
    </label>
  );
}

// --- date helpers (work in local time, then convert to UTC ISO for API) ---
function pad(n) { return String(n).padStart(2, "0"); }

function isoLocalNow() {
  const d = new Date();
  // round up to next 15 min
  const m = d.getMinutes();
  d.setMinutes(m + (15 - (m % 15)) % 15 || 15, 0, 0);
  return localIso(d);
}

function isoLocalPlus(minutes) {
  const d = new Date();
  const m = d.getMinutes();
  d.setMinutes(m + (15 - (m % 15)) % 15 + minutes, 0, 0);
  return localIso(d);
}

function localIso(d) {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function trimToMinute(iso) {
  if (!iso) return "";
  // Accept ISO with seconds/Z and trim to minute precision in *local time*
  // If already in local "YYYY-MM-DDTHH:mm" format, return as-is
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(iso)) return iso;
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return "";
    return localIso(d);
  } catch { return ""; }
}

function toUtcIso(localOrIso) {
  if (!localOrIso) return new Date().toISOString();
  // If it's already a full ISO with timezone, just return it
  if (/Z$|[+-]\d{2}:?\d{2}$/.test(localOrIso)) return localOrIso;
  // Treat as local time, convert to UTC ISO
  const d = new Date(localOrIso);
  if (isNaN(d.getTime())) return new Date().toISOString();
  return d.toISOString();
}
